import type { EChartsOption, LineSeriesOption, MarkAreaComponentOption, MarkLineComponentOption, SeriesOption } from 'echarts'
import {
  buildChartGrid,
  buildDataZoom,
  buildTimeXAxis,
  buildValueYAxis,
  buildMultiSeriesTooltipFormatter,
  type ChartDataZoom,
} from './base-chart-options'

export interface GroupAlarmSeries {
  name: string
  data: [number, number][]
  /** Índice da série na paleta cíclica por sensor (fases do mesmo sensor compartilham a cor). */
  colorIndex: number
  /** Diferencia fases do mesmo sensor (sólido, tracejado, pontilhado, tracejado-pontilhado). */
  dashIndex?: number
}

/** Paleta cíclica por sensor (alinhada às cores do sistema). */
export const GROUP_ALARM_SERIES_COLORS = [
  '#123ADD',
  '#099657',
  '#f97316',
  '#7c3aed',
  '#db2777',
  '#0891b2',
  '#65a30d',
  '#ca8a04',
] as const

const LINE_DASHES: Array<NonNullable<LineSeriesOption['lineStyle']>['type']> = [undefined, [6, 4], [3, 3], [2, 2, 6, 2]]

export interface BuildGroupAlarmChartOptionParams {
  series: GroupAlarmSeries[]
  unit: string
  dataZoom: ChartDataZoom
  /** markArea do período do alarme (faixa vermelha). */
  markArea?: MarkAreaComponentOption
  /** markLine de início/fim do alarme. */
  markLine?: MarkLineComponentOption
  sliderBottom?: number
  showMoveHandle?: boolean
  decimals?: number
}

/** Gráfico de linhas multi-série (uma linha por sensor do grupo) com faixa do alarme. */
export function buildGroupAlarmChartOption(params: BuildGroupAlarmChartOptionParams): EChartsOption {
  const {
    series,
    unit,
    dataZoom,
    markArea,
    markLine,
    sliderBottom = 8,
    showMoveHandle = false,
    decimals = 2,
  } = params

  const colors = GROUP_ALARM_SERIES_COLORS
  const lineSeries: SeriesOption[] = series.map((s, index) => {
    const color = colors[s.colorIndex % colors.length]
    const dash = LINE_DASHES[(s.dashIndex ?? 0) % LINE_DASHES.length]
    return {
      name: s.name,
      type: 'line',
      smooth: false,
      symbol: 'none',
      connectNulls: true,
      data: s.data,
      lineStyle: { width: 2, color, ...(dash ? { type: dash } : {}) },
      itemStyle: { color },
      ...(index === 0 ? { markArea, markLine } : {}),
    }
  })

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      formatter: buildMultiSeriesTooltipFormatter(unit, decimals),
    },
    legend:
      lineSeries.length > 1
        ? { top: 0, left: 0, right: 0, type: 'scroll', textStyle: { fontSize: 10 } }
        : undefined,
    grid: buildChartGrid(),
    dataZoom: buildDataZoom({
      dataZoom,
      accentColor: GROUP_ALARM_SERIES_COLORS[0],
      fillerColor: 'rgba(18, 58, 221, 0.2)',
      sliderBottom,
      showMoveHandle,
    }),
    xAxis: buildTimeXAxis(),
    yAxis: buildValueYAxis({ unit }),
    series: lineSeries,
  }
}
